// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
// backend
import { ORG_TAGS as MAN_ORG_TAGS } from "@/lib/ManufacturingTagsEditor.generated";
import { ORG_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { ORG_TAGS } from "@/lib/CommonTagsEditor.generated";
import type { ITags } from "@/lib/models";
import type { IGetCurrentOrganisationRes } from "@/lib/backend/common.generated.types";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import EntityEditTemplate from "../shared/EntityEditTemplate";
import TagsTables from "@/modules/shared/ui/TagsTables";
import {
    Columns,
    mapDataToCategories,
    mapDataToCustomTags,
    onChangeUpdateTags,
    updateTagsConfig,
    type TagsConfig,
    type TagUpdateValue,
} from "@/modules/shared/ui/ReactTableTags";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import DataFields from "@/modules/shared/ui/DataFields";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

type State = {
    org_data: IGetCurrentOrganisationRes | null;
    gui_tags: ITags;
    man_tags: ITags;
    tags: ITags;
    title: string;
    success: string | null;
    error: string | null;
    weight: number;
};

/**
 * Displaying production lines list.
 */
class OrganisationEdit extends Component<Props, State> {
    state: State = {
        error: null,
        gui_tags: {},
        man_tags: {},
        org_data: null,
        tags: {},
        success: null,
        title: "",
        weight: 0,
    };

    componentDidMount() {
        this.load();
    }

    load = async () => {
        try {
            this.setState({ error: null });
            const man_res = await getBackend2().manufacturing.getCurrentOrganisation({});
            const gui_res = await getBackend2().common.getCurrentOrganisation({});
            this.setState({ org_data: gui_res });
            if (man_res.tags) {
                this.setState({ man_tags: man_res.tags });
            }
            if (gui_res.tags) {
                this.setState({ gui_tags: gui_res.tags });
            }
        } catch (e) {
            this.setState({
                error: translate("common.error", "An error occured while fetching data."),
            });
        }
    };

    onSave = async () => {
        try {
            await getBackend2().manufacturing.updateCurrentOrganisation({ tags: this.state.man_tags });
            await getBackend2().common.updateCurrentOrganisation({ tags: this.state.gui_tags });
            this.setState(
                {
                    error: null,
                    success: translate("common.success_save", "Data saved successfully."),
                },
                () =>
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000)
            );
        } catch (e) {
            this.setState({
                success: null,
                error: translate("common.error_save", "An error occured while saving data."),
            });
        }
    };

    renderError = () => {
        if (this.state.error) {
            return <ErrorComponent msg={this.state.error} type="error" />;
        }
        return null;
    };

    renderSuccess = () => {
        if (this.state.success) {
            return <ErrorComponent msg={this.state.success} type="success" />;
        }
        return null;
    };

    onGuiChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ gui_tags: onChangeUpdateTags(this.state.gui_tags, name, value) });
    };

    onManChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ man_tags: onChangeUpdateTags(this.state.man_tags, name, value) });
    };

    handleInputChange = (event: Event) => {
        if (event.currentTarget instanceof HTMLInputElement || event.currentTarget instanceof HTMLSelectElement) {
            // read new values
            const target = event.currentTarget;
            let value = target.value;
            const name = target.name;

            this.setState(prev_state => {
                prev_state[name] = value;
                return prev_state;
            });
        }
    };

    render() {
        const data: IGetCurrentOrganisationRes = this.state.org_data || {
            active: false,
            code: "-",
            lang: "-",
            tags: {},
            title: "-",
            uuid: "-",
        };
        const data_fields = [
            [{ label: translate("common.title", "Title"), text: data.title }],
            [{ label: translate("common.code", "Code"), text: data.code }],
            [{ label: translate("common.language", "Language"), text: data.lang }],
            [{ label: translate("common.active", "Active"), text: data.active ? "Yes" : "No" }],
            [{ label: translate("common.uuid", "-UUID"), text: data.uuid }],
        ];
        return (
            <EntityEditTemplate onSave={this.onSave} title={this.state.title}>
                {this.renderSuccess()}
                {this.renderError()}
                <div className="mt-3">
                    <h2 className="mb-4">Current organisation</h2>
                    <DataFields rows={data_fields}></DataFields>
                </div>
                <div className="mt-5">
                    <h2 className="mb-4">Manufacturing tags</h2>
                    <TagsTables
                        categories={mapDataToCategories(
                            this.state.man_tags,
                            updateTagsConfig(MAN_ORG_TAGS as TagsConfig),
                            ORG_TAGS_ACCESS
                        )}
                        columns={Columns}
                        onChangeCell={this.onManChangeCell}
                        categoryKeyPrefix="man-"
                        stretchHeight={false}
                        customTags={mapDataToCustomTags(
                            this.state.man_tags,
                            updateTagsConfig(MAN_ORG_TAGS as TagsConfig)
                        )}
                    />
                </div>
                <div className="mt-5">
                    <h2 className="mb-4">Common tags</h2>
                    <TagsTables
                        categories={mapDataToCategories(
                            this.state.gui_tags,
                            updateTagsConfig(ORG_TAGS as TagsConfig),
                            ORG_TAGS_ACCESS
                        )}
                        columns={Columns}
                        onChangeCell={this.onGuiChangeCell}
                        categoryKeyPrefix="gui-"
                    />
                </div>
                {this.renderSuccess()}
                {this.renderError()}
            </EntityEditTemplate>
        );
    }
}

export default OrganisationEdit;
